import React, { useMemo } from 'react';

import { loadEntryPoint, useRelayEnvironment } from 'react-relay';

import type { A2uiSurfaceAdapterRovoProps } from '@atlassian/a2ui-renderer-next/v0_9/a2ui-surface-adapter-rovo';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

// eslint-disable-next-line import/extensions
import { a2uiSurfaceAdapterRovoEntryPoint } from './a2uiSurfaceAdapterRovo.entrypoint';

const ENTRY_POINT_ID = 'a2ui-surface-adapter-rovo';

const A2uiSurfaceEntryPointContainer = ({
	messages,
	onAction,
}: A2uiSurfaceAdapterRovoProps): React.JSX.Element => {
	const relayEnvironment = useRelayEnvironment();

	// The renderer has no queries, so the reference only depends on the
	// environment — `messages`/`onAction` flow through as runtime props below.
	const entryPointReference = useMemo(
		() =>
			loadEntryPoint(
				{ getEnvironment: () => relayEnvironment },
				a2uiSurfaceAdapterRovoEntryPoint,
				{},
			),
		[relayEnvironment],
	);

	return (
		<InternalEntryPointContainer
			id={ENTRY_POINT_ID}
			entryPointReference={entryPointReference}
			runtimeProps={{ messages, onAction }}
			fallback={null}
		/>
	);
};

/**
 * Inline consumer of {@link a2uiSurfaceAdapterRovoEntryPoint}. Provides a Relay
 * environment (required by `EntryPointContainer` even for a query-less entry
 * point) so the renderer can be dropped anywhere in the chat tree.
 */
export const A2uiSurfaceEntryPointRenderer = (
	props: A2uiSurfaceAdapterRovoProps,
): React.JSX.Element => {
	return (
		<AtlassianRelayEnvironmentProvider>
			<A2uiSurfaceEntryPointContainer {...props} />
		</AtlassianRelayEnvironmentProvider>
	);
};
